import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabaseClient'

export default function DeveloperLandingPage() {
  const { developerId } = useParams()

  const { data: developerData, isLoading: developerLoading } = useQuery({
    queryKey: ['developer', developerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', developerId)
        .eq('role', 'developer')
        .maybeSingle()

      if (error) throw error
      return data
    },
  })

  const { data: projectsData, isLoading: projectsLoading } = useQuery({
    queryKey: ['developer-projects', developerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          units:units(count)
        `)
        .eq('developer_id', developerId)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data
    },
    enabled: !!developerId,
  })

  const isLoading = developerLoading || projectsLoading

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner spinner-lg text-primary-600 mb-4"></div>
          <p className="text-secondary-600">Loading developer information...</p>
        </div>
      </div>
    )
  }

  if (!developerData) {
    return (
      <div className="container-custom py-12">
        <div className="alert-error">
          <p>Developer not found.</p>
        </div>
        <Link to="/projects" className="btn-primary mt-4">
          Back to Projects
        </Link>
      </div>
    )
  }

  const developer = developerData
  const projects = projectsData || []
  const currentProjects = projects.filter(p => p.status === 'activated')
  const pastProjects = projects.filter(p => p.status === 'completed')

  const getStatusBadge = (status) => {
    const statusConfig = {
      activated: { label: 'Active', class: 'bg-green-100 text-green-700' },
      draft: { label: 'Draft', class: 'bg-secondary-100 text-secondary-600' },
      completed: { label: 'Completed', class: 'bg-blue-100 text-blue-700' },
    }
    const config = statusConfig[status] || statusConfig.draft
    return (
      <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${config.class}`}>
        {config.label}
      </span>
    )
  }

  return (
    <div className="bg-secondary-50 min-h-screen">
      <div className="bg-white border-b border-secondary-200">
        <div className="container-custom py-12">
          <nav className="mb-6 text-sm">
            <Link to="/projects" className="text-primary-600 hover:text-primary-700">
              Projects
            </Link>
            <span className="mx-2 text-secondary-400">/</span>
            <span className="text-secondary-600">
              {developer.company_name || developer.full_name}
            </span>
          </nav>

          <div className="max-w-4xl">
            <h1 className="text-4xl font-bold text-secondary-900 mb-4">
              {developer.company_name || developer.full_name}
            </h1>

            {developer.company_name && developer.full_name && (
              <p className="text-lg text-secondary-600 mb-4">
                Contact: {developer.full_name}
              </p>
            )}

            <div className="flex flex-wrap gap-4 text-secondary-600">
              {developer.email && (
                <a
                  href={`mailto:${developer.email}`}
                  className="flex items-center hover:text-primary-600 transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {developer.email}
                </a>
              )}
              {developer.phone && (
                <a
                  href={`tel:${developer.phone}`}
                  className="flex items-center hover:text-primary-600 transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  {developer.phone}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container-custom py-8">
        {currentProjects.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-secondary-900 mb-6">
              Current Projects
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentProjects.map((project) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="card p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-semibold text-secondary-900 flex-1">
                      {project.name}
                    </h3>
                    {getStatusBadge(project.status)}
                  </div>

                  <p className="text-secondary-600 flex items-center mb-4">
                    <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {project.location}
                  </p>

                  {project.description && (
                    <p className="text-sm text-secondary-600 mb-4 line-clamp-3">
                      {project.description}
                    </p>
                  )}

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-secondary-600">
                      {project.units[0]?.count || 0} units
                    </span>
                    {project.expected_completion && (
                      <span className="text-secondary-600">
                        Est. {new Date(project.expected_completion).toLocaleDateString('en-NZ', { year: 'numeric', month: 'short' })}
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {pastProjects.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-secondary-900 mb-6">
              Past Projects
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pastProjects.map((project) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="card p-6 hover:shadow-lg transition-shadow opacity-90"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-semibold text-secondary-900 flex-1">
                      {project.name}
                    </h3>
                    {getStatusBadge(project.status)}
                  </div>

                  <p className="text-secondary-600 flex items-center mb-4">
                    <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {project.location}
                  </p>

                  {project.description && (
                    <p className="text-sm text-secondary-600 mb-4 line-clamp-3">
                      {project.description}
                    </p>
                  )}

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-secondary-600">
                      {project.units[0]?.count || 0} units
                    </span>
                    {project.expected_completion && (
                      <span className="text-secondary-600">
                        Completed {new Date(project.expected_completion).toLocaleDateString('en-NZ', { year: 'numeric', month: 'short' })}
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {projects.length === 0 && (
          <div className="text-center py-12 text-secondary-600">
            <p>No projects found for this developer.</p>
          </div>
        )}
      </div>
    </div>
  )
}
